import React, { useContext, useEffect, useState } from "react";
import {
  Paper,
  Text,
  Flex,
  Container,
  Group,
  ActionIcon,
  Image,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconShieldCheck,
  IconX,
} from "@tabler/icons-react";
import { DataContext } from "../context/DataContext";

export default function Navigation({ logo_url, forceHideClose = false }) {
  const context = useContext(DataContext);
  const navigate = useNavigate();
  const isIframe = window !== parent;
  const isWideViewport = useMediaQuery("(min-width: 600px)");
  // Detect wide wordmark logos so we can center them; tight/square logos stay
  // left-aligned. Threshold is ~2.5:1 — anything wider than that is treated as
  // a wordmark.
  const [isWideLogo, setIsWideLogo] = useState(false);
  const handleLogoLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    if (naturalWidth && naturalHeight) {
      setIsWideLogo(naturalWidth / naturalHeight > 2.5);
    }
  };

  // Only destructure if context is available
  const dispatch = context?.dispatch;
  const settings = context?.settings;
  const currentPath = context?.currentPath;
  const postMessage = context?.postMessage;

  // Determine if close button should be shown
  // Default behavior: show X if in iframe
  // Override: if isCloseButtonShown exists and is false, don't show even if in iframe
  // `forceHideClose` is an unconditional caller-side override (e.g.
  // the result screen, where the Done button IS the close ack).
  const shouldHideCloseButton =
    forceHideClose || settings?.isCloseButtonShown === false;

  // Only show X in nav for narrow viewports; wide viewports render it in App.js outside the box
  const showCloseInNav = isIframe && !shouldHideCloseButton && !isWideViewport;

  const handleClose = () => {
    if (dispatch) {
      dispatch({ type: "SET_CLOSED_CLICKED", payload: true });
    }
    postMessage && postMessage("CLOSE");
  };

  const handleBack = () => {
    if (dispatch) {
      dispatch({ type: "SET_SELECTED_COMPONENT", payload: "" });
    }
    navigate(-1);
  };

  // Position rules:
  // - no logo: close button right-aligned (existing behavior)
  // - tight logo + close: logo left, close right (apart)
  // - wide logo + close: invisible spacer left, logo center, close right (apart over 3 items)
  // - tight logo, no close: logo left
  // - wide logo, no close: logo center
  const groupPosition = !logo_url
    ? "right"
    : isWideLogo && !showCloseInNav
    ? "center"
    : "apart";

  return (
    <Group
      position={groupPosition}
      style={{
        height: "30px",
        minHeight: "30px",
        width: "100%",
        ...(showCloseInNav ? { paddingBottom: "8px" } : {}),
      }}
    >
      {/* Invisible spacer to balance the close button so a wide logo lands
          visually centered between two equal-width edges */}
      {isWideLogo && showCloseInNav && <div style={{ width: 28 }} />}

      {logo_url && (
        <img
          src={logo_url}
          alt=""
          onLoad={handleLogoLoad}
          style={{
            height: 22,
            width: "auto",
            maxWidth: 200,
            objectFit: "contain",
          }}
        />
      )}

      {showCloseInNav && (
        <ActionIcon
          variant="light"
          onClick={handleClose}
          // Float above the test-mode banner (zIndex 99999 in root.js) so the
          // close affordance stays clickable when the banner is shown.
          style={{ position: "relative", zIndex: 100000 }}
        >
          <IconX stroke={1.5} size={18} />
        </ActionIcon>
      )}
    </Group>
  );
}
